import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api, getTenantSlug } from '../config/api';
import { Logo } from './common/Logo';
import { TenantFooter } from './common/TenantFooter';
import { UserMenu } from './UserMenu';
import { useTenant } from '../contexts/TenantContext';
import {
  HomeIcon,
  BookOpenIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  BookmarkIcon,
  LinkIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  DocumentArrowUpIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  AcademicCapIcon,
  Square3Stack3DIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { MOCK_INSTITUTE_ID } from '../data/mockAnalyticsData';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { isTenantSubdomain } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Admin-only: show a pending-count badge on the Book Approvals nav item
  // so stale queues are visible without visiting the page.
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const refreshPendingCount = useCallback(async () => {
    if (!user?.is_admin) return;
    try {
      const response = await api.get('/api/books/admin/pending');
      // Endpoint already filters to non-approved statuses server-side, but the
      // badge specifically means "books waiting for action" so we count only
      // pending + in_review (rejected books are terminal).
      const waiting = response.data.filter(
        (b: any) => b.status === 'pending' || b.status === 'in_review',
      ).length;
      setPendingCount(waiting);
    } catch (error) {
      // Non-fatal — badge just doesn't show.
    }
  }, [user?.is_admin]);

  useEffect(() => {
    refreshPendingCount();
    // Refresh on window focus so an admin switching back from another tab
    // sees an up-to-date count without reloading.
    const onFocus = () => refreshPendingCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshPendingCount]);

  // Also refresh whenever the admin navigates to or from the approvals page,
  // since their actions there change the count.
  useEffect(() => {
    if (location.pathname.startsWith('/admin/book-approvals')) {
      refreshPendingCount();
    }
  }, [location.pathname, refreshPendingCount]);

  type NavItem = {
    name: string;
    href: string;
    icon: typeof HomeIcon;
    badge?: number | null;
  };

  // Course Studio is teacher-facing — surface it for platform admins or anyone
  // with a teaching/management role in an institution. (Courses are tenant-
  // scoped, so it only *functions* on an institution subdomain, but we show
  // the link wherever a teacher is signed in.)
  const TEACHING_ROLES = ['institution_admin', 'institution_sub_admin', 'teacher'];
  const canTeach =
    !!user?.is_admin ||
    !!user?.institution_memberships?.some((m: any) => TEACHING_ROLES.includes(m.role));

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Library', href: '/library', icon: BookOpenIcon },
    // Student-facing courses surface — only meaningful inside an institution.
    ...(getTenantSlug() ? [{ name: 'My Courses', href: '/my-courses', icon: Square3Stack3DIcon } as NavItem] : []),
    // My Requests is scoped to uploaders tracking their own submissions;
    // admins upload via the auto-approve path and don't need this surface.
    ...(!user?.is_admin
      ? [{ name: 'My Requests', href: '/my-requests', icon: DocumentArrowUpIcon } as NavItem]
      : []),
    { name: 'Bookmarks', href: '/bookmarks', icon: BookmarkIcon },
    { name: 'Chat', href: '/chat', icon: ChatBubbleLeftRightIcon },
    { name: 'Playbook', href: '/playbook', icon: SparklesIcon },
    ...(canTeach ? [{ name: 'Course Studio', href: '/teacher/courses', icon: AcademicCapIcon } as NavItem] : []),
    ...(canTeach ? [{ name: 'Assignment Studio', href: '/assignment-studio', icon: ClipboardDocumentCheckIcon } as NavItem] : []),
    // Video Library is a shared surface — managers manage it, students watch what's
    // shared with them — so it shows for any member on an institution subdomain
    // (like My Courses), not just teachers.
    ...(getTenantSlug() ? [{ name: 'Content Library', href: '/video-library', icon: VideoCameraIcon } as NavItem] : []),
    ...(user?.is_admin
      ? [
          {
            name: 'Book Approvals',
            href: '/admin/book-approvals',
            icon: ClipboardDocumentCheckIcon,
            badge: pendingCount,
          } as NavItem,
          { name: 'Institutions', href: '/admin/institutions', icon: BuildingOffice2Icon } as NavItem,
        ]
      : (() => {
          // Gate the Institution nav link on two axes:
          //   1. Tenant subdomain  → only show when user has a management
          //      role in *this* tenant. (A JSPT student who's a teacher
          //      elsewhere shouldn't see a link on jspt.localhost that
          //      would bounce to /dashboard.)
          //   2. Platform context  → only show if the subdomain-restriction
          //      flag is OFF. When it's ON (default), institute management
          //      lives on the tenant subdomain only, so the link should be
          //      hidden on platform context to avoid showing a link that
          //      would just redirect away.
          const tenantSlug = getTenantSlug();
          const restrictToSubdomain =
            process.env.REACT_APP_RESTRICT_INSTITUTE_MGMT_TO_SUBDOMAIN !== 'false';
          const MANAGEMENT_ROLES = ['institution_admin', 'institution_sub_admin', 'teacher'];

          let hasInstituteAccess: boolean;
          if (tenantSlug) {
            // Platform admins keep cross-tenant moderation freedom — they
            // can reach institute pages on any subdomain without a
            // membership row, mirroring the backend dependency helpers
            // (require_institution_admin etc.) which already early-return
            // for is_admin. Without this bypass, the Institution nav link
            // would be hidden from admins on tenant subdomains even
            // though they have full backend access to manage the tenant.
            hasInstituteAccess = !!user?.is_admin || !!user?.institution_memberships?.some(
              (m: any) => m.institution_slug === tenantSlug && MANAGEMENT_ROLES.includes(m.role)
            );
          } else if (restrictToSubdomain) {
            // Platform context + flag on → never show.
            hasInstituteAccess = false;
          } else {
            hasInstituteAccess = !!user?.institution_memberships?.some(
              (m: any) => MANAGEMENT_ROLES.includes(m.role)
            );
          }
          return hasInstituteAccess
            ? [{ name: 'Institution', href: '/institution', icon: BuildingOffice2Icon } as NavItem]
            : [];
        })()),
    ...(user?.is_admin ? [{ name: 'Referrals', href: '/admin/referrals', icon: LinkIcon } as NavItem] : []),
    // Analytics is the mock-data demo dashboard — platform-admin-only.
    // Institution admins reach LIVE analytics through the per-cohort
    // Analytics button on InstitutionGroupDetail, not this sidebar link.
    ...(user?.is_admin ? [{ name: 'Analytics', href: `/analytics/${MOCK_INSTITUTE_ID}`, icon: ChartBarIcon } as NavItem] : []),
  ];

  return (
    <div className={`h-screen flex overflow-hidden ${isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'}`}>
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-50 w-64 ${
          isDarkMode 
            ? 'bg-slate-900/95 border-r border-slate-700/50 backdrop-blur-xl' 
            : 'bg-white border-r border-gray-200'
        } transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:inset-auto`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center justify-between h-16 px-6 border-b ${
            isDarkMode ? 'border-slate-700/50' : 'border-gray-200'
          }`}>
            <Link to="/dashboard" className="flex items-center space-x-3">
              <Logo variant="app-icon" size={36} />
              {!isTenantSubdomain && (
                <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  vidya.ai
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`md:hidden ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? isDarkMode
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                      : isDarkMode
                        ? 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${
                    isActive
                      ? isDarkMode ? 'text-cyan-400' : 'text-cyan-700'
                      : isDarkMode ? 'text-slate-400 group-hover:text-white' : 'text-gray-500'
                  }`} />
                  <span className="font-medium flex-1">{item.name}</span>
                  {typeof item.badge === 'number' && item.badge > 0 && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
                        isActive
                          ? 'bg-cyan-500 text-white'
                          : isDarkMode
                            ? 'bg-amber-500/20 text-amber-300'
                            : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Bottom section — clickable user card opens preferences popover */}
          <div className={`relative border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
            <UserMenu
              isOpen={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              onLogout={handleLogout}
            />
            <button
              type="button"
              onClick={() => setUserMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              className={`w-full px-4 py-4 flex items-center gap-3 text-left transition-colors ${
                isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'
              }`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 15px rgba(6, 182, 212, 0.25)',
                }}
              >
                <span className="text-white font-semibold">
                  {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {user?.full_name || 'User'}
                </p>
                <p className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {user?.email}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className={`border-b ${
          isDarkMode 
            ? 'bg-slate-900/80 border-slate-700/50 backdrop-blur-xl' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between px-4 h-16">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className={`md:hidden ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            
            <div className="flex-1" />
          </div>
        </header>

        {/* Page content */}
        <main className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'}`}>
          {children}
        </main>
        <TenantFooter />
      </div>
    </div>
  );
};

export default Layout;
